'use client';

import { ReactNode, createContext, useContext } from 'react';
import { AppearanceImage } from '@/utility/appearanceConfig';

const AppearanceConfigContext = createContext<Record<string, AppearanceImage>>({});

/**
 * Provides a deduplicated appearance lookup to the product cards beneath it.
 * The map is serialized into the RSC payload once instead of repeating the full
 * appearance objects on every card. Cards opt in by passing `appearanceIds`
 * instead of `appearances`.
 */
export const AppearanceConfigProvider = ({
  appearances,
  children,
}: {
  appearances: Record<string, AppearanceImage>;
  children: ReactNode;
}) => (
  <AppearanceConfigContext.Provider value={appearances}>
    {children}
  </AppearanceConfigContext.Provider>
);

/** Resolves appearance ids to their full data via the nearest provider. */
export const useResolvedAppearances = (ids: string[] | undefined): AppearanceImage[] => {
  const config = useContext(AppearanceConfigContext);
  if (!ids) return [];
  return ids.map((id) => config[id]).filter((appearance) => appearance !== undefined);
};
